import React, { PureComponent } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { firebaseConnect, getVal } from 'react-redux-firebase';
import { Link, navigate } from 'gatsby';
import { bindActionCreators, compose } from 'redux';
import styled, { css } from 'styled-components';

import Heading, { SubHeading } from '../../components/heading';
import Indicator from '../../components/indicator';
import SelectPlace from './select-place';
import TimeslotSelector from '../../components/timeslot-selector';
import { GAME_ID, GAME_SERIES, TIMESLOT_ID } from '../../constants';
import withDatabase from '../../hocs/with-database';
import { selectTimeslot } from '../../modules/checkout/action-creators';
import { gameChanged } from '../../modules/global/action-creators';

import jaLocale from './ja.locale';
import { getLocalizedDate } from './selectors';

const Section = styled.div`
  padding: 1em;
  align-items: center;
  justify-content: space-around;
  display: flex;
  flex: 1;
  width: 100%;
  @media (max-width: 767px) {
    display: block;
    width: initial;
    margin: 0 auto;
  }
`;

const Details = styled.div`
  margin: 2em 0 2.5em;
  > * {
    margin-bottom: 0.8em;
  }
`;

const SpreadRow = styled.div`
  display: flex;
  justify-content: space-around;
`;

const GameInfo = styled.div`
  display: grid;
  grid-gap: 0.5em;
  grid-template-columns: 1fr 1fr;
`;

const GameInfoLabel = styled.div`
  justify-self: flex-end;
`;

const GameInfoValue = styled.div`
  justify-self: flex-start;
`;

const WarningText = styled.div`
  white-space: pre-line;
  font-size: 0.8em;
  color: ${props => (props.highlight ? props.theme.primary : 'inherit')};
`;

const Info = styled.div`
  max-width: 30em;
  @media (min-width: 768px) {
    margin-right: 1em;
  }
`;

const GameTabs = styled.div`
  display: flex;
`;

const VariableTimeWarning = styled.div`
  grid-column: 1/3;
  text-align: center;
  font-size: 0.7em;
  margin-bottom: 1em;
`;

const GameTab = styled(Link)`
  flex: 1;
  padding: 1em 0;
  text-align: center;
  border-top-left-radius: 0.2em;
  border-top-right-radius: 0.2em;
  background: ${props => (props.active ? '#282828' : '#171717')};
  color: inherit;
  text-decoration: none;

  ${props =>
    props.active
      ? css`
          font-weight: bold;
        `
      : css`
          cursor: pointer;
        `};

  &:not(:last-child) {
    margin-right: 2rem;
  }
`;

class BookingPage extends PureComponent {
  constructor(props) {
    super(props);

    let is_select_place = false;
    if (this.props.gameId == '6') {
      const queryParams = new URLSearchParams(location.search);
      is_select_place = queryParams.get('is_selected') || false;
    } else {
      is_select_place = true;
    }
    //    console.log("is_select_place:" + is_select_place);
    this.state = {
      is_select_place: is_select_place,
      gameId: this.props.gameId
    };
    this.props.addTranslationForLanguage(jaLocale, 'ja');
  }
  handleTimeChange = (scale, date) => {
    navigate(
      `/games/${
        this.props.gameId
      }/${scale}/?is_selected=true&time=${date.unix()}`
    );
  };

  handleTimeslotSelect = timeslot => {
    this.props.selectTimeslot(timeslot);
    const queryParams = new URLSearchParams();
    queryParams.set(GAME_ID, this.props.gameId);
    queryParams.set(TIMESLOT_ID, timeslot.key);
    navigate(`/checkout/tickets?${queryParams.toString()}`);
  };

  onClickSelectPlace = gameId => {
    location.href = `/games/${gameId}/calendar/?is_selected=true`;
  };

  render() {
    const { game, gameId, pageContext, location } = this.props;
    const bookingClosed = game && game.bookingClosed;
    return (
      <Section>
        <Info>
          <Heading>
            <Translate id="bookingPage.title" />
          </Heading>
          <SubHeading>
            <Translate id="bookingPage.subtitle" />
          </SubHeading>
          <Details>
            {!game && (
              <SpreadRow>
                <Indicator spin />
              </SpreadRow>
            )}
            {game && (
              <GameInfo>
                <GameInfoLabel>
                  <Translate id={`bookingPage.duration.${gameId}`} />
                </GameInfoLabel>
                <GameInfoValue>
                  <div>{game.gameDuration}</div>
                </GameInfoValue>
                <VariableTimeWarning>
                  <Translate id="bookingPage.variableTimeWarning" />
                </VariableTimeWarning>
                <GameInfoLabel>
                  <Translate id="bookingPage.ageWarning" />
                </GameInfoLabel>
                <GameInfoValue>{game.ageRating}</GameInfoValue>
              </GameInfo>
            )}
            {bookingClosed && (
              <WarningText highlight>{game.closedExplanation}</WarningText>
            )}
          </Details>
        </Info>
        {!bookingClosed && (
          <div>
            {GAME_SERIES[gameId] && (
              <GameTabs>
                {Object.values(GAME_SERIES[gameId]).map(
                  ({ gameId: id, label }) => (
                    <GameTab
                      active={Number(id === gameId)}
                      key={id}
                      to={`/games/${id}/${pageContext.scale}/${location.search}`}
                    >
                      {label}
                    </GameTab>
                  )
                )}
              </GameTabs>
            )}
            {this.state.is_select_place && (
              <TimeslotSelector
                date={this.props.date}
                game={game}
                onTimeChange={this.handleTimeChange}
                onTimeslotSelect={this.handleTimeslotSelect}
                scale={pageContext.scale}
              />
            )}
            {!this.state.is_select_place && (
              <SelectPlace gameId={gameId} onClick={this.onClickSelectPlace} />
            )}
          </div>
        )}
      </Section>
    );
  }
}

/**
 * Map Redux State to props
 */
const mapStateToProps = (state, props) => ({
  gameId: state.global.gameId,
  date: getLocalizedDate(state, props),
  game: getVal(state.firebase.data, `/games/${state.global.gameId}`)
});

/**
 * Map Redux Dispatch to props
 */
const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(
    {
      selectTimeslot,
      gameChanged
    },
    dispatch
  )
});

export default compose(
  withDatabase,
  withLocalize,
  connect(mapStateToProps, mapDispatchToProps),
  firebaseConnect(({ gameId }) => [`/games/${gameId}`])
)(BookingPage);
